/**
 * API Client utility for making requests to the backend
 * Handles both cookie-based and token-based authentication
 */

import { errorReportingService } from '@/services/errorReportingService';

interface ApiRequestOptions extends RequestInit {
  includeAuth?: boolean;
}

class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.baseUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api/v1';
    this.setupTokenRefreshTimer();
  }

  /**
   * Setup automatic token refresh timer
   */
  private setupTokenRefreshTimer() {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }

    // Check token expiry every minute
    this.refreshTimer = setInterval(() => {
      this.checkAndRefreshToken();
    }, 60000) as any; // Check every minute

    // Also check on initialization
    this.checkAndRefreshToken();
  }

  /**
   * Check if token needs refresh and refresh if necessary
   */
  private async checkAndRefreshToken() {
    const isAuthenticated = localStorage.getItem('is_authenticated');
    if (isAuthenticated !== 'true') return;

    const tokenExpiry = localStorage.getItem('token_expiry');
    if (!tokenExpiry) {
      // If no expiry is stored, assume 30 minutes from now
      const defaultExpiry = Date.now() + 30 * 60 * 1000;
      localStorage.setItem('token_expiry', defaultExpiry.toString());
      return;
    }

    const expiryTime = parseInt(tokenExpiry);
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    // Refresh if less than 5 minutes until expiry
    if (timeUntilExpiry < 5 * 60 * 1000) {
      console.log('Token expiring soon, refreshing...');
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Update expiry time (assume 30 minutes for access token)
        const newExpiry = Date.now() + 30 * 60 * 1000;
        localStorage.setItem('token_expiry', newExpiry.toString());
      }
    }
  }

  /**
   * Make an authenticated API request
   */
  async request<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    const { includeAuth = true, ...requestOptions } = options;

    // Default headers
    const headers: HeadersInit = {
      ...requestOptions.headers,
    };

    // Add content-type if not present and we have a body
    if (requestOptions.body && !(requestOptions.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Build final request options
    const finalOptions: RequestInit = {
      ...requestOptions,
      headers,
      credentials: includeAuth ? 'include' : 'same-origin', // Always include cookies for authenticated requests
    };

    // For backwards compatibility, also include Authorization header if we have a token
    // This will be removed once all backend endpoints are updated
    const token = localStorage.getItem('auth_token');
    if (token && includeAuth) {
      (headers as any)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, finalOptions);

    // Handle 401 errors - session expired
    if (response.status === 401 && includeAuth) {
      // Try to refresh the token
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry the original request
        return this.request(endpoint, options);
      } else {
        // Clear auth state and redirect to login
        localStorage.removeItem('is_authenticated');
        localStorage.removeItem('user_data');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/auth';
        throw new Error('Session expired');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      const errorMessage = error.detail || `Request failed: ${response.statusText}`;

      // Log API errors to error service
      await errorReportingService.reportApiError(
        errorMessage,
        response.status,
        {
          endpoint,
          method: finalOptions.method || 'GET',
          severity: response.status >= 500 ? 'error' : 'warning',
        }
      );

      throw new Error(errorMessage);
    }

    // Return JSON response if content-type is JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return response.text() as any;
  }

  /**
   * Refresh the authentication token
   */
  private async refreshToken(): Promise<boolean> {
    // If already refreshing, wait for the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Create new refresh promise
    this.refreshPromise = this.doRefreshToken();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      // Clear the promise after completion
      this.refreshPromise = null;
    }
  }

  /**
   * Actually perform the token refresh
   */
  private async doRefreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');

      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: refreshToken ? JSON.stringify({ refresh_token: refreshToken }) : '{}',
      });

      if (response.ok) {
        // Cookies are automatically updated by the backend
        console.log('Token refreshed successfully');
        return true;
      }

      console.error('Token refresh failed with status:', response.status);
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // Convenience methods
  async get<T = any>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T = any>(endpoint: string, body?: any, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  async put<T = any>(endpoint: string, body?: any, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  async delete<T = any>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  async patch<T = any>(endpoint: string, body?: any, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  /**
   * Get current authenticated user
   * Replaces: backendClient.auth.getUser()
   * @deprecated_supabase
   */
  async getCurrentUser() {
    try {
      const user = await this.get('/auth/me');
      return { data: { user }, error: null };
    } catch (error: any) {
      if (error.message.includes('401') || error.message.includes('Session expired')) {
        return { data: { user: null }, error: { message: 'Not authenticated' } };
      }
      return { data: { user: null }, error: { message: error.message } };
    }
  }

  /**
   * Generate audio from script
   * Replaces: backendClient.functions.invoke('generate-audio')
   * @deprecated_supabase
   * @deprecated This method is not used anymore. Audio generation is handled by the async job queue.
   * The /meditation/generate endpoint handles both content AND audio generation.
   */
  async generateAudio(params: {
    script: string;
    voice_id?: string;
    background_music_url?: string;
    voice_gender?: string;
    latency_tolerance?: number;
    [key: string]: any;
  }) {
    // NOTE: This endpoint doesn't exist. Audio is generated by the meditation worker.
    // This method is kept for backward compatibility but should not be called.
    throw new Error('generateAudio is deprecated. Use /meditation/generate async job queue instead.');
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export class for testing or custom instances
export default ApiClient;
